import type { OrgLike } from "@features/plugins/orgPlugins";
import {
  type PluginHookContribution,
  definePluginHook,
} from "@features/plugins/pluginHook";

import type { protectedRoute } from "./types";

export type ProtectedRoutesPluginContext = {
  isMobile: boolean;
};

export type PluginProtectedRoutesHook = {
  readonly id: string;
  contribute(
    fn: (
      context: ProtectedRoutesPluginContext
    ) => readonly protectedRoute[] | void
  ): PluginHookContribution;
  compose(
    organization: OrgLike,
    context: ProtectedRoutesPluginContext
  ): protectedRoute[];
};

function routeKey(route: protectedRoute): string | undefined {
  if (route.index === true || route.path === "") {
    return "index";
  }

  return route.path;
}

function assertUniqueRouteKeys(
  hookId: string,
  routes: readonly protectedRoute[]
): protectedRoute[] {
  const seen = new Set<string>();

  for (const route of routes) {
    const key = routeKey(route);
    if (key !== undefined) {
      if (seen.has(key)) {
        throw new Error(`Duplicate route "${key}" in plugin hook "${hookId}".`);
      }

      seen.add(key);
    }

    if (route.children) {
      assertUniqueRouteKeys(hookId, route.children);
    }
  }

  return [...routes];
}

/**
 * Defines a host-owned extension point for authenticated routes.
 *
 * Contributions become children of the app layout, inserted after host routes
 * and before the 404 splat. Returning `void` skips this context (e.g. mobile).
 * React Router still ranks by specificity, so a more specific plugin path can
 * outrank a parameterized host route.
 */
export function definePluginProtectedRoutesHook(config: {
  name: string;
}): PluginProtectedRoutesHook {
  const routesHook = definePluginHook<
    ProtectedRoutesPluginContext,
    readonly protectedRoute[]
  >({
    name: config.name,
    initial: () => [],
    merge: (current, next) => [...current, ...next],
  });

  return {
    id: config.name,
    contribute(fn) {
      return routesHook.contribute(fn);
    },
    compose(organization, context) {
      return assertUniqueRouteKeys(
        config.name,
        routesHook.run(organization, context)
      );
    },
  };
}

export const protectedRoutesHook = definePluginProtectedRoutesHook({
  name: "routing.protectedRoutes",
});
